import { CampaignProps } from 'src/types/campaign';

export const FOLDER_PATH_DELIMITER = '_sub_';

/** react-beautiful-dnd types — folders and campaigns cannot be mixed. */
export const FOLDER_DND_TYPE = 'CAMPAIGN_FOLDER';
export const CAMPAIGN_DND_TYPE = 'CAMPAIGN';

export const FOLDER_PARENT_DROPPABLE_PREFIX = 'folder-parent:';
export const FOLDER_DRAGGABLE_PREFIX = 'folder:';

export function folderParentDroppableId(parentPath: string): string {
  return `${FOLDER_PARENT_DROPPABLE_PREFIX}${normalizeFolderPath(parentPath)}`;
}

export function parseFolderParentDroppableId(id: string): string | null {
  if (!id.startsWith(FOLDER_PARENT_DROPPABLE_PREFIX)) {
    return null;
  }
  return normalizeFolderPath(id.slice(FOLDER_PARENT_DROPPABLE_PREFIX.length));
}

export function folderDraggableId(path: string): string {
  return `${FOLDER_DRAGGABLE_PREFIX}${normalizeFolderPath(path)}`;
}

export function parseFolderDraggableId(id: string): string | null {
  if (!id.startsWith(FOLDER_DRAGGABLE_PREFIX)) {
    return null;
  }
  return normalizeFolderPath(id.slice(FOLDER_DRAGGABLE_PREFIX.length));
}

export type FolderNode = {
  name: string;
  path: string;
  children: FolderNode[];
  campaigns: CampaignProps[];
};

export type FlatFolderItem = {
  node: FolderNode;
  depth: number;
};

/** Flatten visible folder nodes for a single Droppable (rbd does not support nesting). */
export function flattenFolderTree(
  nodes: FolderNode[],
  openPaths: Set<string>,
  depth = 0
): FlatFolderItem[] {
  const result: FlatFolderItem[] = [];
  nodes.forEach((node) => {
    result.push({ node, depth });
    if (openPaths.has(node.path) && node.children.length > 0) {
      result.push(...flattenFolderTree(node.children, openPaths, depth + 1));
    }
  });
  return result;
}

export function splitFolderPath(path: string): string[] {
  const trimmed = path?.trim() ?? '';
  if (!trimmed) {
    return [];
  }
  return trimmed
    .split(FOLDER_PATH_DELIMITER)
    .map((segment) => segment.trim())
    .filter(Boolean);
}

export function joinFolderPath(segments: string[]): string {
  return segments
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join(FOLDER_PATH_DELIMITER);
}

export function normalizeFolderPath(path: string): string {
  return joinFolderPath(splitFolderPath(path));
}

/** Folder display names must be non-empty and must not contain the path delimiter. */
export function isValidFolderName(name: string): boolean {
  const trimmed = name.trim();
  return trimmed.length > 0 && !trimmed.includes(FOLDER_PATH_DELIMITER);
}

export function getParentPath(path: string): string {
  const segments = splitFolderPath(path);
  if (segments.length <= 1) {
    return '';
  }
  return joinFolderPath(segments.slice(0, -1));
}

export function getFolderLeafName(path: string): string {
  const segments = splitFolderPath(path);
  return segments[segments.length - 1] || '';
}

export function isPathEqual(a: string, b: string): boolean {
  return normalizeFolderPath(a) === normalizeFolderPath(b);
}

/** True when `path` is a strict descendant of `ancestor`. */
export function isDescendantPath(path: string, ancestor: string): boolean {
  const normalized = normalizeFolderPath(path);
  const anc = normalizeFolderPath(ancestor);
  if (!anc) {
    return splitFolderPath(normalized).length > 0;
  }
  if (!normalized || isPathEqual(normalized, anc)) {
    return false;
  }
  return normalized.startsWith(`${anc}${FOLDER_PATH_DELIMITER}`);
}

export function isUnderPath(path: string, ancestor: string): boolean {
  return isPathEqual(path, ancestor) || isDescendantPath(path, ancestor);
}

export function rewritePathPrefix(
  path: string,
  oldPrefix: string,
  newPrefix: string
): string {
  const normalized = normalizeFolderPath(path);
  const oldNorm = normalizeFolderPath(oldPrefix);
  const newNorm = normalizeFolderPath(newPrefix);

  if (!oldNorm) {
    return normalized;
  }
  if (isPathEqual(normalized, oldNorm)) {
    return newNorm;
  }
  if (isDescendantPath(normalized, oldNorm)) {
    const rest = splitFolderPath(normalized).slice(
      splitFolderPath(oldNorm).length
    );
    return newNorm
      ? joinFolderPath([...splitFolderPath(newNorm), ...rest])
      : joinFolderPath(rest);
  }
  return normalized;
}

function ensureNode(roots: FolderNode[], segments: string[]): FolderNode {
  let list = roots;
  let node: FolderNode | undefined;
  const pathParts: string[] = [];

  for (const segment of segments) {
    pathParts.push(segment);
    const path = joinFolderPath(pathParts);
    node = list.find((child) => child.name === segment);
    if (!node) {
      node = { name: segment, path, children: [], campaigns: [] };
      list.push(node);
    }
    list = node.children;
  }

  return node!;
}

/**
 * Builds a nested folder tree from campaign.folder path strings.
 * `emptyPaths` are local-only folders with no campaigns yet.
 */
export function buildFolderTree(
  campaigns: CampaignProps[],
  emptyPaths: string[] = []
): { roots: FolderNode[]; uncategorized: CampaignProps[] } {
  const roots: FolderNode[] = [];
  const uncategorized: CampaignProps[] = [];

  campaigns.forEach((item) => {
    const segments = splitFolderPath(item.folder ?? '');
    if (!segments.length) {
      uncategorized.push(item);
      return;
    }
    const node = ensureNode(roots, segments);
    node.campaigns.push(item);
  });

  emptyPaths.forEach((emptyPath) => {
    const segments = splitFolderPath(emptyPath);
    if (segments.length) {
      ensureNode(roots, segments);
    }
  });

  return { roots, uncategorized };
}

export function collectCampaignsUnderPath(
  campaigns: CampaignProps[],
  path: string
): CampaignProps[] {
  return campaigns.filter((item) => isUnderPath(item.folder ?? '', path));
}

export function uniqueSiblingFolderName(
  existingNames: string[],
  baseName = 'New Folder'
): string {
  const nameSet = new Set(existingNames.map((n) => n.trim()));
  if (!nameSet.has(baseName)) {
    return baseName;
  }
  for (let index = 1; index < 10000; index += 1) {
    const candidate = `${baseName} ${index}`;
    if (!nameSet.has(candidate)) {
      return candidate;
    }
  }
  return `${baseName} ${Date.now()}`;
}

export function getChildFolderNames(
  roots: FolderNode[],
  parentPath: string
): string[] {
  if (!parentPath) {
    return roots.map((node) => node.name);
  }
  const segments = splitFolderPath(parentPath);
  let list = roots;
  let node: FolderNode | undefined;
  for (const segment of segments) {
    node = list.find((child) => child.name === segment);
    if (!node) {
      return [];
    }
    list = node.children;
  }
  return node ? node.children.map((child) => child.name) : [];
}
